<template>
  <div
    class="sb-minibrowser"
    aria-live="polite"
    :class="{
      'sb-minibrowser--embedded': isEmbedded,
      'sb-minibrowser--loading': isLoading,
    }"
  >
    <SbMinibrowserSearch
      v-if="!hideSearch"
      :data-testid="`${dataTestid}-search`"
      :model-value="searchInput"
      :placeholder="placeholder"
      @update:model-value="handleSearchInput"
      @keydown="handleSearchKeydown"
    />

    <slot v-if="$slots.filter" name="filter" />

    <div class="sb-minibrowser__list-container">
      <div v-show="isLoading" class="sb-minibrowser__loading">
        <SbSkeleton v-for="n in 4" :key="n" height="20px" />
      </div>
      <SbMinibrowserBreadcrumbs
        v-if="hasBreadcrumbs"
        :items="internalBreadcrumbs"
        :data-testid="`${dataTestid}-breadcrumbs`"
      />

      <template v-if="hasGroupedItems">
        <template v-for="(groupItem, index) in groupedItems">
          <SbMinibrowserList
            v-if="!$slots.list"
            :key="index"
            v-bind="groupItem"
            :data-testid="`${dataTestid}-list`"
          />

          <slot name="list" v-bind="groupItem" />
        </template>
      </template>

      <template v-if="hasOtherItems">
        <SbMinibrowserList
          v-if="!$slots.list"
          :items="otherItems"
          :data-testid="`${dataTestid}-additional-list`"
        />

        <slot name="list" :items="otherItems" />
      </template>
    </div>

    <p
      v-if="hasNotFilteredElements"
      class="sb-minibrowser__not-found"
      :data-testid="`${dataTestid}-not-found-text`"
    >
      <slot name="empty-search" :search-input="searchInput" />
      <SbEmptyState v-if="!$slots['empty-search']" v-bind="emptyStateProps">
        <template #description>
          {{
            'Try adjusting your search or filter to find what you are looking for.'
          }}
        </template>
      </SbEmptyState>
    </p>
  </div>
</template>

<script lang="ts" setup>
import {
  ref,
  computed,
  provide,
  onUnmounted,
  onMounted,
  useId,
  watch,
  nextTick,
} from 'vue'
import { debounce } from 'throttle-debounce'

import { flatten } from '../../utils'
import { useDropdown } from '../Dropdown'
import SbSkeleton from '../Skeleton/SbSkeleton.vue'
import SbEmptyState from '../EmptyState/SbEmptyState.vue'
import SbMinibrowserSearch from './components/MinibrowserSearch.vue'
import SbMinibrowserList from './components/MinibrowserList.vue'
import SbMinibrowserBreadcrumbs from './components/MinibrowserBreadcrumbs.vue'
import type { MinibrowserLoadMorePayload, MinibrowserOption } from './types'
import type { BreadcrumbItem } from '../Breadcrumbs/types'

export type MinibrowserProps = {
  /**
   * Indicate that the minibrowser is loading
   */
  isLoading?: boolean

  /**
   * Indicate that the minibrowser is embedded, removing padding, borders, shadows and make the component full width
   */
  isEmbedded?: boolean

  /**
   * Breadcrumbs to be displayed on the top of the minibrowser
   */
  breadcrumbs?: BreadcrumbItem[]

  /**
   * Debounce time to filter the items. Default is 300 milliseconds
   */
  filterDebounce?: number

  /**
   * Prefix to be displayed when no items are found
   */
  notFoundPrefix?: string

  /**
   * List of options to be displayed on the minibrowser
   */
  options: MinibrowserOption[]

  /**
   * Placeholder to be displayed on the search input
   */
  placeholder?: string

  /**
   * Indicate that the search input should be cleared when an item is selected
   */
  clearOnSelect?: boolean

  /**
   * Indicate that the search input should be hidden
   */
  hideSearch?: boolean

  /**
   * It will disable the selection of the item when the user press Enter on the search input
   */
  preventEnterOnSearch?: boolean

  /**
   * Define the load more state
   */
  loadMore?: MinibrowserLoadMorePayload

  /**
   * Data test id attribute
   */
  // eslint-disable-next-line vue/prop-name-casing
  'data-testid'?: string
}

const props = withDefaults(defineProps<MinibrowserProps>(), {
  breadcrumbs: undefined,
  filterDebounce: 300,
  notFoundPrefix: 'No matches for',

  loadMore: () => ({
    visible: false,
    current: 0,
    total: 0,
    loading: false,
  }),
  // input properties
  placeholder: 'Search content items',
  'data-testid': 'sb-mini-browser',
})

const emit = defineEmits<{
  (e: 'clear-navigation'): void
  (e: 'close'): void
  (e: 'filter', value: { value: string }): void
  (e: 'navigate', index: number): void
  (e: 'select-item', item: Partial<MinibrowserOption>): void
  (e: 'load-more'): void
}>()

defineSlots<{
  filter(): void
  list(props: MinibrowserOption): void
  'empty-search'(props: { searchInput: string }): void
}>()

const filterHandler = ref<Function | undefined>(undefined)
const searchInput = ref('')
const browserListId = `sb-minibrowser-list-${useId()}`
const root = ref<HTMLUListElement | null>(null)
const {
  activatedMenuItemId,
  isOpen,
  loadListItems,
  focusOnFirstItem,
  focusOnLastItem,
  updateTabIndex,
  handleKeydownOnList,
} = useDropdown({
  root,
})

const dataTestid = computed(() => props['data-testid'] || 'sb-mini-browser')
const shouldFocusOnInput = ref(false)
const focusedOnLastItem = ref(false)
const browserContext = computed(() => () => ({
  // browser states
  browserListId,
  activatedMenuItemId,
  isLoading: props.isLoading || false,
  shouldFocusOnInput,
  loadMore: props.loadMore,

  // browser methods
  clearNavigation,
  navigateTo,
  selectItem,
  focusOnFirstItem,
  focusOnLastItem,
  handleKeydownOnList,
  updateTabIndex,
  loadMoreItems,
}))

const internalItems = computed(() => {
  if (props.isLoading) {
    return []
  }

  return [...(props.options || [])]
})

const hasNotFilteredElements = computed(
  () =>
    !props.isLoading &&
    internalItems.value?.length === 0 &&
    searchInput.value?.length > 0,
)

const notFoundText = computed(
  () => `${props.notFoundPrefix} "${searchInput.value}"`,
)

const emptyStateProps = computed(() => {
  return {
    illustrationLink:
      'https://a.storyblok.com/f/136045/726x790/94d533d075/no-search-results.svg',
    title: notFoundText.value,
  }
})

const groupedItems = computed(() =>
  internalItems.value.filter((item) => item.group),
)

const hasGroupedItems = computed(() => groupedItems.value.length > 0)

const otherItems = computed(() =>
  internalItems.value.filter((item) => !item.group),
)

const hasOtherItems = computed(() => otherItems.value.length > 0)

const hasBreadcrumbs = computed(() => internalBreadcrumbs.value.length > 0)

const internalBreadcrumbs = computed(() => [...(props.breadcrumbs || [])])

provide('browserContext', browserContext.value)

watch(internalItems, () => {
  nextTick(() => {
    root.value = document.querySelector(`#${browserListId}`)
    loadListItems()
    if (focusedOnLastItem.value) {
      focusedOnLastItem.value = false
      return
    }
    if (!props.isEmbedded) {
      shouldFocusOnInput.value = true
    }
  })
})

onUnmounted(() => {
  filterHandler.value = undefined
})

onMounted(() => {
  filterHandler.value = debounce(props.filterDebounce, () => {
    emit('filter', { value: searchInput.value })
  })
  root.value = document.querySelector(`#${browserListId}`)
  isOpen.value = true
  loadListItems()
})

const clearNavigation = () => {
  emit('clear-navigation')
}

const handleSearchInput = (value: string) => {
  searchInput.value = value

  filterHandler.value && filterHandler.value()
}

const handleSearchKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    emit('close')
  } else if (event.key === 'Enter' && !props.preventEnterOnSearch) {
    selectFilteredItem()
  }
}

const selectFilteredItem = () => {
  let items = [] as MinibrowserOption[]

  if (
    hasGroupedItems.value &&
    groupedItems.value.length === 1 &&
    groupedItems.value[0]?.items?.length === 1
  ) {
    const branchItem = getDeepLeaf(groupedItems.value[0]?.items[0])
    if (branchItem) {
      items = [...items, branchItem]
    }
  } else if (hasOtherItems.value && otherItems.value.length === 1) {
    items = [...items, ...otherItems.value]
  }

  const itemsFlatted = flatten(items, 'items')

  if (itemsFlatted.length === 1) {
    selectItem(itemsFlatted[0])
  }
}

const getDeepLeaf = (
  branchItem: MinibrowserOption | undefined,
): MinibrowserOption | undefined => {
  if (branchItem?.items?.length === 0) {
    return branchItem
  }

  if (branchItem?.items?.length && branchItem?.items?.length > 1) {
    return undefined
  }

  return getDeepLeaf(branchItem?.items && branchItem?.items[0])
}

const navigateTo = (index = 0) => {
  if (props.isLoading) {
    return
  }
  emit('navigate', index)
}

const selectItem = (item: Partial<MinibrowserOption>) => {
  if (props.isLoading) {
    return
  }
  if (props.clearOnSelect || item.isParent) {
    searchInput.value = ''
  }

  emit('select-item', item)
}

const loadMoreItems = () => {
  if (props.isLoading) {
    return
  }
  focusedOnLastItem.value = true
  focusOnLastItem()
  emit('load-more')
}

defineExpose({
  handleSearchInput,
})
</script>
